import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Icon from '../../../components/AppIcon';

const ToolbarButton = ({ onClick, active, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => { e?.preventDefault(); onClick(); }}
    className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded transition-colors text-sm font-medium
      ${active
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
      }`}
    title={title}
    aria-label={title}
  >
    {children}
  </button>
);

const RichTextToolbar = ({ editor }) => {
  if (!editor) return null;

  const handleLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor?.chain()?.focus()?.setLink({ href: url })?.run();
    }
  };

  const handleImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor?.chain()?.focus()?.setImage({ src: url })?.run();
    }
  };

  return (
    <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-1 flex-wrap">
      {/* History */}
      <ToolbarButton onClick={() => editor?.chain()?.focus()?.undo()?.run()} title="Undo" active={false}>
        <Icon name="Undo" size={16} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor?.chain()?.focus()?.redo()?.run()} title="Redo" active={false}>
        <Icon name="Redo" size={16} />
      </ToolbarButton>
      <div className="w-px h-6 bg-border mx-1" />
      {/* Text formatting */}
      <ToolbarButton
        onClick={() => editor?.chain()?.focus()?.toggleBold()?.run()}
        active={editor?.isActive('bold')}
        title="Bold"
      >
        <Icon name="Bold" size={16} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor?.chain()?.focus()?.toggleItalic()?.run()}
        active={editor?.isActive('italic')}
        title="Italic"
      >
        <Icon name="Italic" size={16} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor?.chain()?.focus()?.toggleUnderline()?.run()}
        active={editor?.isActive('underline')}
        title="Underline"
      >
        <Icon name="Underline" size={16} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor?.chain()?.focus()?.toggleStrike()?.run()}
        active={editor?.isActive('strike')}
        title="Strikethrough"
      >
        <Icon name="Strikethrough" size={16} />
      </ToolbarButton>
      <div className="w-px h-6 bg-border mx-1" />
      {/* Headings */}
      <ToolbarButton
        onClick={() => editor?.chain()?.focus()?.toggleHeading({ level: 1 })?.run()}
        active={editor?.isActive('heading', { level: 1 })}
        title="Heading 1"
      >
        <span className="text-xs font-bold">H1</span>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor?.chain()?.focus()?.toggleHeading({ level: 2 })?.run()}
        active={editor?.isActive('heading', { level: 2 })}
        title="Heading 2"
      >
        <span className="text-xs font-bold">H2</span>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor?.chain()?.focus()?.toggleHeading({ level: 3 })?.run()}
        active={editor?.isActive('heading', { level: 3 })}
        title="Heading 3"
      >
        <span className="text-xs font-bold">H3</span>
      </ToolbarButton>
      <div className="w-px h-6 bg-border mx-1" />
      {/* Lists */}
      <ToolbarButton
        onClick={() => editor?.chain()?.focus()?.toggleBulletList()?.run()}
        active={editor?.isActive('bulletList')}
        title="Bullet List"
      >
        <Icon name="List" size={16} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor?.chain()?.focus()?.toggleOrderedList()?.run()}
        active={editor?.isActive('orderedList')}
        title="Numbered List"
      >
        <Icon name="ListOrdered" size={16} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor?.chain()?.focus()?.toggleBlockquote()?.run()}
        active={editor?.isActive('blockquote')}
        title="Blockquote"
      >
        <Icon name="Quote" size={16} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor?.chain()?.focus()?.toggleCodeBlock()?.run()}
        active={editor?.isActive('codeBlock')}
        title="Code Block"
      >
        <Icon name="Code" size={16} />
      </ToolbarButton>
      <div className="w-px h-6 bg-border mx-1" />
      {/* Media */}
      <ToolbarButton onClick={handleLink} active={editor?.isActive('link')} title="Insert Link">
        <Icon name="Link" size={16} />
      </ToolbarButton>
      <ToolbarButton onClick={handleImage} title="Insert Image" active={false}>
        <Icon name="Image" size={16} />
      </ToolbarButton>
      <div className="w-px h-6 bg-border mx-1" />
      {/* Alignment */}
      <ToolbarButton
        onClick={() => editor?.chain()?.focus()?.setHorizontalRule()?.run()}
        title="Horizontal Rule"
        active={false}
      >
        <Icon name="Minus" size={16} />
      </ToolbarButton>
    </div>
  );
};

const RichTextEditor = ({ content, onChange, placeholder = 'Start typing your content here...' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link?.configure({ openOnClick: false, HTMLAttributes: { class: 'text-primary underline' } }),
      Image?.configure({ HTMLAttributes: { class: 'max-w-full rounded-lg my-4' } }),
      Placeholder?.configure({ placeholder }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange?.(editor?.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'min-h-[500px] outline-none focus:outline-none prose prose-slate max-w-none prose-headings:font-headline prose-headings:text-foreground prose-p:text-foreground prose-p:leading-relaxed prose-a:text-primary prose-strong:text-foreground prose-ul:text-foreground prose-ol:text-foreground prose-li:text-foreground prose-li:marker:text-muted-foreground prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-4 prose-blockquote:italic prose-code:bg-muted prose-code:px-1 prose-code:rounded',
      },
    },
  });

  // Sync external content changes (e.g. version restore)
  useEffect(() => {
    if (editor && content !== editor?.getHTML()) {
      editor?.commands?.setContent(content || '', false);
    }
  }, [content]);

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <RichTextToolbar editor={editor} />
      <div className="flex-1 overflow-y-auto bg-background">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
};

export default RichTextEditor;
